import { LogPhotoStrip } from "../../components/LogPhotoStrip";
import { StrengthSetsSummary } from "../../components/coaching/StrengthSetsSummary";
import { formatDateShort } from "../../lib/coaching";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, ArrowUpRight, Search, X, MessageCircle, CheckCircle2, AlertTriangle, XCircle, Activity as ActivityIcon, RefreshCw, DollarSign, Save, Pencil } from "lucide-react";
import InstallAppPrompt from "../../components/InstallAppPrompt";
import {
  listCoachingAthletes,
  createCoachingAthlete,
  getDigestStatusesBulk,
  getPortalStatusesBulk,
  getWeekVolumeBulk,
  getCoachPlanningStatus,
  coachActivityFeed,
  coachPostWorkoutComment,
  getStudioSettings,
  updateStudioSettings,
} from "../../lib/api";
import { toast } from "sonner";
import CoachingTabs from "../../components/coaching/CoachingTabs";
import PaymentStatusBadge from "../../components/coaching/PaymentStatusBadge";
import CoachPushSettings from "../../components/coaching/CoachPushSettings";
import WeekGlanceTable from "../../components/coaching/WeekGlanceTable";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function PortalStatusChip({ status, hasEmail }) {
  /* status: undefined → draft; 'invited'; 'active' */
  const effective = status || "draft";
  const cfg =
    effective === "active"
      ? {
          label: "Portal active",
          cls: "text-emerald-700 bg-[#eaf5ee] border-emerald-700/30",
        }
      : effective === "invited"
      ? {
          label: "Invite sent",
          cls: "text-[var(--tir-navy)] bg-[#e9eef6] border-[var(--tir-navy)]/25",
        }
      : hasEmail
      ? {
          label: "Ready to invite",
          cls: "text-[var(--tir-burgundy)] bg-[#fbeae9] border-[var(--tir-burgundy)]/30",
        }
      : {
          label: "Draft · building",
          cls: "text-[var(--tir-muted)] bg-[var(--tir-ivory)] border-[var(--tir-border)]",
        };
  return (
    <span
      className={`inline-flex items-center text-[9px] tracking-[0.22em] uppercase font-semibold px-2 py-0.5 border ${cfg.cls}`}
      data-testid={`portal-status-${effective}`}
    >
      {cfg.label}
    </span>
  );
}

function EngagementDot({ status }) {
  /* status: undefined (never sent) | sent | delivered | opened | clicked | bounced | complained | failed */
  if (!status) {
    return (
      <span
        title="Weekly digest not sent yet"
        className="inline-block h-2.5 w-2.5 rounded-full bg-[#D6CFC3]"
        data-testid="engagement-dot-none"
      />
    );
  }
  const map = {
    opened: { color: "#2F855A", label: "Opened the last digest" },
    clicked: { color: "#2F855A", label: "Clicked the last digest" },
    delivered: { color: "#A8A099", label: "Delivered, not yet opened" },
    sent: { color: "#A8A099", label: "Sent" },
    delayed: { color: "#D69E2E", label: "Delivery delayed" },
    bounced: { color: "#7A1F2B", label: "Bounced — check email address" },
    complained: { color: "#7A1F2B", label: "Marked as spam" },
    failed: { color: "#7A1F2B", label: "Send failed" },
  };
  const m = map[status] || { color: "#A8A099", label: status };
  return (
    <span
      title={m.label}
      style={{ background: m.color }}
      className="inline-block h-2.5 w-2.5 rounded-full"
      data-testid={`engagement-dot-${status}`}
    />
  );
}

function DefaultPaymentLinkControl() {
  // Small inline control that exposes the studio-wide default Stripe payment
  // link. Rendered above the athletes list so the coach can update it in one
  // spot; individual athletes may still override it on their profile.
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [link, setLink] = useState("");
  const [initial, setInitial] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const s = await getStudioSettings();
      setLink(s.default_payment_link || "");
      setInitial(s.default_payment_link || "");
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const s = await updateStudioSettings({ default_payment_link: link.trim() });
      setInitial(s.default_payment_link || "");
      toast.success("Default payment link saved");
      setOpen(false);
    } catch {
      toast.error("Couldn't save default payment link");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="default-payment-link-btn"
        title="Studio-wide Stripe payment link used when an athlete has no per-profile override"
        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-navy)] hover:text-[var(--tir-ink)] border border-[var(--tir-border)] hover:border-[var(--tir-navy)] px-3 py-1.5 transition-colors"
      >
        <DollarSign size={11} /> Default payment link
      </button>
    );
  }

  return (
    <div
      className="w-full sm:w-[520px] bg-white border border-[var(--tir-border)] rounded-md p-4 shadow-sm"
      data-testid="default-payment-link-panel"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-burgundy)]">
          Studio default payment link
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>
      <p className="text-xs text-[var(--tir-muted)] mb-2 leading-relaxed">
        Used on invite emails + locked-program screens whenever an athlete has
        no per-profile override. Change this once and it applies studio-wide.
      </p>
      {loading ? (
        <div className="text-xs text-[var(--tir-muted)] py-2">Loading…</div>
      ) : (
        <>
          <input
            type="url"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://buy.stripe.com/..."
            data-testid="default-payment-link-input"
            className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none font-mono-tir"
          />
          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={save}
              disabled={saving || link === initial}
              data-testid="default-payment-link-save"
              className="inline-flex items-center gap-1.5 border border-[var(--tir-burgundy)] bg-[var(--tir-burgundy)] text-white px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold hover:bg-[#5e1521] disabled:opacity-60 transition-colors"
            >
              <Save size={12} /> {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-ink)] px-2 py-1"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}



export default function CoachingAthletesPage() {
  const [athletes, setAthletes] = useState([]);
  const [digestMap, setDigestMap] = useState({});
  const [portalMap, setPortalMap] = useState({});
  const [volumeMap, setVolumeMap] = useState({});
  const [planningMap, setPlanningMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    goal: "",
    target_race: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [paidFilter, setPaidFilter] = useState("all"); // "all" | "unpaid"
  const navigate = useNavigate();

  const refresh = async () => {
    setLoading(true);
    try {
      const [data, statuses, portal, volume, planning] = await Promise.all([
        listCoachingAthletes(),
        getDigestStatusesBulk().catch(() => ({})),
        getPortalStatusesBulk().catch(() => ({})),
        getWeekVolumeBulk().catch(() => ({})),
        getCoachPlanningStatus().catch(() => ({})),
      ]);
      setAthletes(data);
      setDigestMap(statuses || {});
      setPortalMap(portal || {});
      setVolumeMap(volume || {});
      setPlanningMap(planning || {});
    } catch {
      toast.error("Couldn't load coaching athletes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // Surface Strava OAuth callback errors as a toast when the user lands
  // here from `/api/strava/auth/callback`. Common causes:
  //   - `link_expired_or_used` — state was consumed twice or the OAuth
  //     round-trip crossed envs (preview ↔ production).
  //   - `exchange_failed` — Strava code exchange failed.
  //   - anything else from Strava (e.g. "access_denied").
  // Friendlier copy than raw query-string codes so the coach knows what
  // to do next.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const err = sp.get("strava_error");
    if (!err) return;
    const friendly =
      err === "link_expired_or_used"
        ? "That Strava link was already used or has expired. Generate a fresh Connect link and try again."
        : err === "missing_params"
          ? "Strava sent us back without the right info. Generate a fresh Connect link and try again."
          : err === "exchange_failed"
            ? "Strava couldn't complete the connection. Try again in a moment, or generate a fresh Connect link."
            : err === "access_denied"
              ? "You declined the Strava permissions. To sync runs, please approve them next time."
              : `Strava link failed: ${err}`;
    toast.error(friendly, { duration: 8000 });
    sp.delete("strava_error");
    const qs = sp.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (qs ? `?${qs}` : "")
    );
  }, []);

  const q = searchTerm.trim().toLowerCase();
  const searchFiltered = q
    ? athletes.filter(
        (a) =>
          (a.name || "").toLowerCase().includes(q) ||
          (a.email || "").toLowerCase().includes(q)
      )
    : athletes;
  const unpaidCount = useMemo(
    () => athletes.filter((a) => a.paid_active === false).length,
    [athletes]
  );
  const filtered =
    paidFilter === "unpaid"
      ? searchFiltered.filter((a) => a.paid_active === false)
      : searchFiltered;

  const sorted = useMemo(() => {
    const list = [...filtered];
    const byName = (a, b) => (a.name || "").localeCompare(b.name || "");
    if (sortBy === "name_asc") list.sort(byName);
    else if (sortBy === "name_desc") list.sort((a, b) => byName(b, a));
    else if (sortBy === "recent")
      list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return list;
  }, [filtered, sortBy]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      const a = await createCoachingAthlete(form);
      toast.success(`Added ${a.name}`);
      setShowAdd(false);
      setForm({ name: "", email: "", goal: "", target_race: "", notes: "" });
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        Array.isArray(detail)
          ? detail.map((d) => d.msg || d).join(" · ")
          : (detail || "Failed to add athlete")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="max-w-7xl mx-auto px-6 lg:px-10 py-12"
      data-testid="coaching-athletes-page"
    >
      <CoachingTabs className="mb-8" />

      <InstallAppPrompt />

      <section className="mb-8 border-b border-[var(--tir-ink)] pb-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-4">
              Coaching Studio · 1:1 Athletes
            </div>
            <h1 className="font-serif-display text-5xl md:text-6xl leading-[1.05] text-[var(--tir-ink)]">
              Design the week. Send the plan.
            </h1>
            <p className="text-base text-[var(--tir-muted)] mt-5 leading-relaxed max-w-xl">
              Your private roster of 1-on-1 coaching clients. Build their weekly or monthly
              programs here, then email branded PDFs straight from the studio.
            </p>
          </div>
          <div className="flex items-start gap-3 flex-col sm:flex-row sm:items-center">
            <CoachPushSettings />
            <button
              onClick={() => setShowAdd(true)}
              data-testid="add-coaching-athlete-btn"
              className="inline-flex items-center gap-2 bg-[var(--tir-burgundy)] text-white px-6 py-3 text-sm tracking-wider uppercase font-medium hover:bg-[var(--tir-burgundy-dark)] transition-colors"
            >
              <Plus size={16} /> Add athlete
            </button>
          </div>
        </div>
      </section>

      {/* Quick-jump athlete tiles — horizontal scroll row pinned just under
          the hero so the coach can hop straight into any athlete without
          scrolling past the table below. */}
      <AthleteQuickTiles athletes={athletes} portalMap={portalMap} planningMap={planningMap} loading={loading} />

      <WeekGlanceTable />

      {/* Recent activity moved to its own page (/coaching/activity) —
          reachable from the mobile bottom nav + a link here. */}
      <div className="mb-10">
        <Link
          to="/coaching/activity"
          data-testid="open-activity-page-link"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-navy)] hover:text-[var(--tir-ink)] border border-[var(--tir-navy)]/30 hover:border-[var(--tir-navy)] px-4 py-2.5 rounded-md transition-colors"
        >
          <MessageCircle size={13} /> Recent activity — logs & notes
        </Link>
      </div>

      {showAdd && (
        <form
          onSubmit={submit}
          className="mb-10 border border-[var(--tir-ink)] bg-white p-6"
          data-testid="add-coaching-athlete-form"
        >
          <div className="font-serif-display text-2xl mb-5">New coaching athlete</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Name
              </label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none"
                data-testid="coaching-athlete-name-input"
                placeholder="Sarah Vestby"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Email <span className="normal-case tracking-normal text-[10px] text-[var(--tir-muted)]/70 font-normal">— optional, add later to invite to portal</span>
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none"
                data-testid="coaching-athlete-email-input"
                placeholder="leave blank for now"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Goal
              </label>
              <input
                value={form.goal}
                onChange={(e) => setForm({ ...form, goal: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none"
                placeholder="Sub-4hr marathon"
                data-testid="coaching-athlete-goal-input"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Target race
              </label>
              <input
                value={form.target_race}
                onChange={(e) => setForm({ ...form, target_race: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none"
                placeholder="Boston Marathon · Apr 2026"
                data-testid="coaching-athlete-race-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 mt-6">
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-coaching-athlete-btn"
              className="bg-[var(--tir-burgundy)] text-white px-6 py-2.5 text-sm tracking-wider uppercase font-medium hover:bg-[var(--tir-burgundy-dark)] transition-colors disabled:opacity-50"
            >
              {submitting ? "Saving..." : "Save athlete"}
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="text-sm tracking-wider uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <section>
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-serif-display text-3xl">Your athletes</h2>
          <span className="text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)]">
            {q ? `${filtered.length} of ${athletes.length}` : `${athletes.length} total`}
          </span>
        </div>

        {athletes.length > 0 && (
          <div className="flex flex-col md:flex-row md:items-end gap-3 md:gap-6 mb-3">
            <div className="relative flex-1">
              <Search
                size={16}
                className="absolute left-0 top-1/2 -translate-y-1/2 text-[var(--tir-muted)] pointer-events-none"
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email"
                data-testid="coaching-search-input"
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] pl-7 pr-8 py-2.5 text-base focus:outline-none"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[var(--tir-muted)] hover:text-[var(--tir-ink)] p-1"
                  aria-label="Clear"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <div className="md:w-64 shrink-0">
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Sort by
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                data-testid="coaching-sort-select"
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1.5 text-base focus:outline-none cursor-pointer"
              >
                <option value="recent">Recently added</option>
                <option value="name_asc">Name (A → Z)</option>
                <option value="name_desc">Name (Z → A)</option>
              </select>
            </div>
          </div>
        )}

        {athletes.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="paid-filter-row">
            <button
              type="button"
              onClick={() => setPaidFilter("all")}
              data-testid="paid-filter-all"
              className={`px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold border transition-colors ${
                paidFilter === "all"
                  ? "bg-[var(--tir-ink)] text-white border-[var(--tir-ink)]"
                  : "text-[var(--tir-muted)] border-[var(--tir-border)] hover:text-[var(--tir-ink)] hover:border-[var(--tir-ink)]"
              }`}
            >
              All ({athletes.length})
            </button>
            <button
              type="button"
              onClick={() => setPaidFilter("unpaid")}
              data-testid="paid-filter-unpaid"
              className={`px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold border transition-colors inline-flex items-center gap-1.5 ${
                paidFilter === "unpaid"
                  ? "bg-[var(--tir-burgundy)] text-white border-[var(--tir-burgundy)]"
                  : "text-[var(--tir-burgundy)] border-[var(--tir-burgundy)]/40 hover:border-[var(--tir-burgundy)]"
              }`}
            >
              <AlertTriangle size={11} /> Unpaid ({unpaidCount})
            </button>
            <div className="ml-auto">
              <DefaultPaymentLinkControl />
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-[var(--tir-muted)] py-12 text-center">Loading…</div>
        ) : athletes.length === 0 ? (
          <div className="border border-dashed border-[var(--tir-border)] py-16 text-center">
            <div className="font-serif-display text-xl text-[var(--tir-ink)]">
              No coaching athletes yet
            </div>
            <div className="text-sm text-[var(--tir-muted)] mt-2">
              Add your first client to start building their program.
            </div>
          </div>
        ) : sorted.length === 0 ? (
          <div className="border border-dashed border-[var(--tir-border)] py-12 text-center">
            <div className="font-serif-display text-xl text-[var(--tir-ink)]">
              No matches for "{searchTerm}"
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-[var(--tir-border)] border-t border-b border-[var(--tir-border)]">
            {sorted.map((a) => {
              const vol = volumeMap[a.id] || {};
              const km = Number(vol.km) || 0;
              const mins = Number(vol.minutes) || 0;
              const hrs = mins / 60;
              const hasVolume = km > 0.05 || mins > 0.5;
              return (
              <li key={a.id} data-testid={`coaching-athlete-row-${a.id}`}>
                <Link
                  to={`/coaching/athletes/${a.id}`}
                  className="grid grid-cols-12 gap-4 py-5 px-2 hover:bg-white transition-colors group"
                >
                  <div className="col-span-12 md:col-span-4">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <EngagementDot status={digestMap[a.id]?.status} />
                      <div className="font-serif-display text-2xl text-[var(--tir-ink)] group-hover:text-[var(--tir-burgundy)] transition-colors">
                        {a.name}
                      </div>
                      <PortalStatusChip
                        status={portalMap[a.id]}
                        hasEmail={!!(a.email || "").trim()}
                      />
                      {a.paid_active === false && <PaymentStatusBadge paid={false} />}
                      {a.is_active === false && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 text-[9px] tracking-[0.22em] uppercase font-semibold border text-[var(--tir-muted)] bg-[var(--tir-ivory)] border-[var(--tir-border)]"
                          data-testid="inactive-badge"
                          title="Coaching complete or paused — hidden from Quick Jump"
                        >
                          Inactive
                        </span>
                      )}
                    </div>
                    {a.email ? (
                      <div className="text-sm text-[var(--tir-muted)] font-mono-tir mt-0.5 ml-[18px]">
                        {a.email}
                      </div>
                    ) : (
                      <div className="text-[11px] tracking-[0.22em] uppercase text-[var(--tir-muted)] mt-0.5 ml-[18px] italic">
                        no email yet — add one to enable the invite
                      </div>
                    )}
                  </div>
                  <div className="col-span-6 md:col-span-3 text-sm text-[var(--tir-muted)]">
                    {a.goal ? (
                      <>
                        <div className="text-[10px] tracking-[0.22em] uppercase">Goal</div>
                        <div className="truncate text-[var(--tir-ink)]">{a.goal}</div>
                      </>
                    ) : null}
                  </div>
                  <div className="col-span-6 md:col-span-2 text-sm text-[var(--tir-muted)]">
                    {a.target_race ? (
                      <>
                        <div className="text-[10px] tracking-[0.22em] uppercase">
                          Target race
                        </div>
                        <div className="truncate text-[var(--tir-ink)]">
                          {a.target_race}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-[10px] tracking-[0.22em] uppercase">Added</div>
                        <div>{formatDate(a.created_at)}</div>
                      </>
                    )}
                  </div>
                  <div
                    className="col-span-6 md:col-span-2 text-sm text-[var(--tir-muted)]"
                    data-testid={`athlete-week-volume-${a.id}`}
                  >
                    <div className="text-[10px] tracking-[0.22em] uppercase">This week</div>
                    {hasVolume ? (
                      <div className="text-[var(--tir-ink)] font-mono-tir leading-tight">
                        {km > 0.05 && (
                          <span data-testid={`athlete-week-km-${a.id}`}>
                            {km >= 10 ? Math.round(km) : km.toFixed(1)} km
                          </span>
                        )}
                        {km > 0.05 && mins > 0.5 && (
                          <span className="text-[var(--tir-muted)]"> · </span>
                        )}
                        {mins > 0.5 && (
                          <span data-testid={`athlete-week-hours-${a.id}`}>
                            {hrs >= 1
                              ? `${hrs.toFixed(1)} h`
                              : `${Math.round(mins)} min`}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="italic text-[var(--tir-muted)]">—</div>
                    )}
                  </div>
                  <div className="col-span-12 md:col-span-1 flex items-center md:justify-end text-[var(--tir-burgundy)]">
                    <ArrowUpRight
                      size={20}
                      className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                    />
                  </div>
                </Link>
              </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * AthleteQuickTiles — horizontal scroll-row of athlete cards under the
 * coaching-hub hero. Lets the coach hop straight into any athlete with a
 * single tap, without scrolling past the (much longer) sortable table.
 *
 * Each tile = initials avatar + short name + portal status chip.
 */
function AthleteQuickTiles({ athletes, portalMap, planningMap, loading }) {
  const navigate = useNavigate();
  // Only surface CURRENTLY-ACTIVE athletes in the quick jump strip so the
  // coach doesn't scroll past retired / wrapped-up clients every time. Legacy
  // athletes with `is_active` undefined default to true (visible).
  const activeAthletes = (athletes || []).filter((a) => a.is_active !== false);
  if (loading) {
    return (
      <div className="mb-8" data-testid="quick-tiles-loading">
        <div className="text-[10px] tracking-[0.22em] uppercase font-mono-tir text-[var(--tir-muted)] mb-2">
          Quick jump
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="w-32 h-24 bg-[var(--tir-ivory)] border border-[var(--tir-border)] rounded-md animate-pulse" />
          ))}
        </div>
      </div>
    );
  }
  if (!activeAthletes.length) return null;
  // Stable initials helper.
  const initials = (n) =>
    (n || "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() || "")
      .join("") || "·";
  // Planning-status band colour derived from weeks_planned_ahead.
  // Threshold matches the backend push nudge (2 weeks).
  //   0–1 weeks → red (urgent, coach is one week from running out)
  //   2–3 weeks → yellow (comfortable but not luxurious)
  //   4+ weeks → green (plenty of runway)
  //   unknown / no program yet → neutral (no band)
  const planningTone = (weeks) => {
    if (weeks == null) return null;
    if (weeks >= 4) return { band: "#2F855A", label: "wks ahead", tone: "green" };
    if (weeks >= 2) return { band: "#B7791F", label: "wks ahead", tone: "yellow" };
    return { band: "#9B2C2C", label: weeks === 1 ? "wk left" : "wks left", tone: "red" };
  };
  return (
    <section className="mb-8" data-testid="athlete-quick-tiles">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-muted)] font-mono-tir">
          Quick jump · {activeAthletes.length} active
        </h2>
        {/* Legend so the coach can decode the colour band at a glance.
            Only shown when at least one athlete has planning data. */}
        {Object.keys(planningMap || {}).length > 0 && (
          <div className="hidden sm:flex items-center gap-3 text-[9px] tracking-[0.18em] uppercase font-mono-tir text-[var(--tir-muted)]" data-testid="planning-legend">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-sm" style={{ background: "#2F855A" }} /> 4+ wks
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-sm" style={{ background: "#B7791F" }} /> 2–3 wks
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-sm" style={{ background: "#9B2C2C" }} /> &lt; 2 wks
            </span>
          </div>
        )}
      </div>
      {/* Horizontal scroll-row — flex-nowrap so tiles stay on a single line
          and the coach swipes/scrolls sideways instead of wrapping. */}
      <div
        className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1"
        style={{ scrollbarWidth: "thin" }}
      >
        {activeAthletes.map((a) => {
          const pStatus = portalMap[a.id]?.status;
          const chipColor =
            pStatus === "active"
              ? "#2F855A"
              : pStatus === "invited"
                ? "var(--tir-navy)"
                : "var(--tir-muted)";
          const planning = planningMap?.[a.id];
          const weeks = planning ? Number(planning.weeks_planned_ahead || 0) : null;
          const tone = planningTone(weeks);
          return (
            <Link
              key={a.id}
              to={`/coaching/athletes/${a.id}`}
              data-testid={`quick-tile-${a.id}`}
              data-planning-tone={tone?.tone || "none"}
              className="group shrink-0 w-44 bg-white border border-[var(--tir-border)] rounded-md hover:shadow-md hover:border-[var(--tir-burgundy)]/40 transition-all p-3 relative overflow-hidden"
              style={
                tone
                  ? { boxShadow: `inset 4px 0 0 0 ${tone.band}` }
                  : undefined
              }
            >
              <div className="flex items-center gap-2.5 mb-2">
                <div
                  className="w-10 h-10 rounded-full bg-[var(--tir-burgundy)] text-white flex items-center justify-center font-serif-display text-base tracking-wide shrink-0"
                  aria-hidden="true"
                >
                  {initials(a.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-[var(--tir-ink)] truncate group-hover:text-[var(--tir-burgundy)] transition-colors">
                    {a.name || "Unnamed"}
                  </div>
                  {a.target_race && (
                    <div className="text-[10px] text-[var(--tir-muted)] truncate font-mono-tir">
                      {a.target_race}
                    </div>
                  )}
                </div>
              </div>
              {/* Bottom row: portal-status chip + planning weeks chip.
                  Kept as a flex row so both fit even on narrow phones. */}
              <div className="flex items-center justify-between gap-2">
                {(pStatus === "active" || pStatus === "invited") ? (
                  <div className="text-[9px] tracking-[0.18em] uppercase font-semibold flex items-center gap-1.5" style={{ color: chipColor }}>
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full"
                      style={{ background: chipColor }}
                    />
                    {pStatus === "active" ? "Portal active" : "Invite sent"}
                  </div>
                ) : (
                  <span />
                )}
                {tone && (
                  <div
                    className="text-[9px] tracking-[0.16em] uppercase font-semibold font-mono-tir px-1.5 py-0.5 rounded-sm"
                    style={{ background: `${tone.band}18`, color: tone.band }}
                    data-testid={`planning-weeks-${a.id}`}
                    title={`${weeks} week${weeks === 1 ? "" : "s"} of Done Editing ahead`}
                  >
                    {weeks} {tone.label}
                  </div>
                )}
              </div>
              {planning?.next_unplanned_week_start && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (planning.program_id) {
                      navigate(`/coaching/programs/${planning.program_id}/edit?week=${planning.next_unplanned_week_number || 1}`);
                    } else {
                      navigate(`/coaching/athletes/${a.id}`);
                    }
                  }}
                  data-testid={`planning-next-${a.id}`}
                  title="Open this week in the program editor"
                  className="mt-2 w-full flex items-center justify-between text-[9px] tracking-[0.16em] uppercase font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)] border-t border-dashed border-[var(--tir-border)] pt-1.5"
                >
                  <span className="text-[var(--tir-muted)]">Plan next</span>
                  <span className="font-mono-tir">
                    {planning.next_unplanned_week_number ? `W${planning.next_unplanned_week_number} · ` : ""}
                    {formatDateShort(planning.next_unplanned_week_start)} →
                  </span>
                </button>
              )}
            </Link>
          );
        })}
      </div>
    </section>
  );
}

/**
 * ActivityFeedBlock — reverse-chronological feed of recent workout logs +
 * athlete-side comments across the whole roster. Pulls from
 * /api/coaching/activity-feed (defaults: last 14 days, up to 50 items).
 * The coach can scan everyone's recent activity in one place.
 */
/** Module-level helpers used by ActivityFeedBlock + FeedGroupCard +
 *  FeedItemBody. Hoisted out of the block so the FeedGroupCard
 *  component (defined at module scope to keep React DevTools tidy) can
 *  use them too. */
function fmtTs(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffH = (now - d) / 36e5;
    if (diffH < 24) return `${Math.max(1, Math.round(diffH))}h ago`;
    const diffD = Math.round(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}
function StatusBadge({ s }) {
  if (s === "complete") return <CheckCircle2 size={14} className="text-emerald-700 shrink-0" />;
  if (s === "partial") return <AlertTriangle size={14} className="text-amber-600 shrink-0" />;
  if (s === "skipped" || s === "missed") return <XCircle size={14} className="text-[var(--tir-burgundy)] shrink-0" />;
  return null;
}

export function ActivityFeedBlock() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // Filter: "all" = full feed, "awaiting" = only athlete comments where
  // the coach hasn't replied yet (i.e. the threads that need attention).
  const [filter, setFilter] = useState("all");

  // Fetch the feed. `silent=true` skips the loading-spinner so the
  // background poll doesn't flash the empty-state every minute.
  const refresh = React.useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const data = await coachActivityFeed(14, 30);
      setItems(data?.items || []);
      setError(null);
      setLastUpdated(new Date());
    } catch {
      setError("Couldn't load activity feed");
    } finally {
      if (!silent) setRefreshing(false);
      setLoading(false);
    }
  }, []);

  // Initial load + auto-refresh: poll every 60s while the tab is
  // visible AND on window focus. Combined with the periodic Strava sync
  // server-side, the coach now sees new athlete activity within a
  // minute of it being logged — no manual refresh needed.
  useEffect(() => {
    refresh(true);
    const id = setInterval(() => {
      if (document.visibilityState === "visible") refresh(true);
    }, 60_000);
    const onFocus = () => refresh(true);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const filteredItems = useMemo(() => {
    if (filter !== "awaiting") return items;
    return items.filter((it) => it.kind === "comment" && it.awaiting_reply);
  }, [items, filter]);

  // Group items by (athlete + workout_date) so multi-workout days
  // collapse into a single card. Each group preserves:
  //   - the original feed order (already sorted by workout_date DESC,
  //     submission ts DESC server-side),
  //   - the timestamp of the *newest* item in the group for the header,
  //   - all logs and notes so an expanded card shows everything inline.
  // For "Awaiting reply" filter view we skip grouping — those notes are
  // each an actionable conversation and the coach wants them itemised.
  const groupedItems = useMemo(() => {
    if (filter === "awaiting") {
      return filteredItems.map((it) => ({ items: [it], single: true, key: `${it.kind}-${it.athlete_id}-${it.ts || ""}` }));
    }
    const groups = [];
    const indexByKey = new Map();
    for (const it of filteredItems) {
      // Items missing a workout_date can't be grouped meaningfully — give
      // them a unique key so they stay individual rows.
      const key = it.workout_date && it.athlete_id
        ? `${it.athlete_id}|${it.workout_date}`
        : `__solo|${it.kind}|${it.ts || ""}|${Math.random()}`;
      if (indexByKey.has(key)) {
        groups[indexByKey.get(key)].items.push(it);
      } else {
        indexByKey.set(key, groups.length);
        groups.push({ items: [it], key });
      }
    }
    return groups.map((g) => ({ ...g, single: g.items.length === 1 }));
  }, [filteredItems, filter]);
  const awaitingCount = useMemo(
    () => items.filter((it) => it.kind === "comment" && it.awaiting_reply).length,
    [items]
  );

  return (
    <section className="mb-12" data-testid="coach-activity-feed">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className="font-serif-display text-3xl tracking-wide text-[var(--tir-ink)] leading-none">
            Recent activity
          </h2>
          <div className="text-[10px] tracking-[0.22em] uppercase font-mono-tir text-[var(--tir-muted)] mt-1.5 flex items-center gap-2 flex-wrap">
            <span>Last 14 days · across all athletes</span>
            {lastUpdated && (
              <span data-testid="feed-last-updated" className="text-[var(--tir-muted)]/80">
                · updated {(() => {
                  const diff = Math.round((Date.now() - lastUpdated.getTime()) / 60000);
                  if (diff < 1) return "just now";
                  if (diff < 60) return `${diff}m ago`;
                  return lastUpdated.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
                })()}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => refresh(false)}
            disabled={refreshing}
            data-testid="feed-refresh"
            title="Refresh now"
            className="p-1.5 text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] disabled:opacity-50 border border-[var(--tir-border)] bg-white rounded-sm inline-flex items-center justify-center"
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
          </button>
          <div
            className="relative inline-flex items-center border border-[var(--tir-border)] bg-white rounded-sm overflow-hidden"
            role="tablist"
            aria-label="Activity feed filter"
          >
            <button
              type="button"
              onClick={() => setFilter("all")}
              data-testid="feed-filter-all"
              role="tab"
              aria-selected={filter === "all"}
              className={`relative z-[1] px-3 py-1 text-[10px] tracking-[0.18em] uppercase font-semibold transition-colors ${
                filter === "all"
                  ? "bg-[var(--tir-navy)] text-white"
                  : "text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
              }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setFilter("awaiting")}
              data-testid="feed-filter-awaiting"
              role="tab"
              aria-selected={filter === "awaiting"}
              className={`relative z-[1] px-3 py-1 text-[10px] tracking-[0.18em] uppercase font-semibold border-l border-[var(--tir-border)] transition-colors inline-flex items-center gap-1.5 ${
                filter === "awaiting"
                  ? "bg-[var(--tir-burgundy)] text-white"
                  : "text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
              }`}
            >
              Awaiting reply
              {awaitingCount > 0 && (
                <span
                  className={`inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 rounded-full text-[9px] font-bold ${
                    filter === "awaiting"
                      ? "bg-white text-[var(--tir-burgundy)]"
                      : "bg-[var(--tir-burgundy)] text-white"
                  }`}
                  data-testid="feed-filter-awaiting-count"
                >
                  {awaitingCount}
                </span>
              )}
            </button>
          </div>
          <div className="text-[10px] font-mono-tir text-[var(--tir-muted)]">
            {groupedItems.length} {filter === "awaiting" ? "item" : "card"}{groupedItems.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>
      <div className="bg-[var(--tir-ivory)] rounded-lg border border-[var(--tir-border)] divide-y divide-[var(--tir-border)] overflow-hidden">
        {loading ? (
          <div className="p-6 text-[12px] italic text-[var(--tir-muted)]">Loading…</div>
        ) : error ? (
          <div className="p-6 text-[12px] italic text-[var(--tir-burgundy)]">{error}</div>
        ) : filteredItems.length === 0 ? (
          <div className="p-8 text-center">
            <ActivityIcon size={20} className="text-[var(--tir-muted)] mx-auto mb-2" />
            <div className="text-[13px] text-[var(--tir-muted)]">
              {filter === "awaiting"
                ? "All caught up — no athlete notes are waiting on a reply."
                : "No activity yet this fortnight."}
            </div>
            {filter !== "awaiting" && (
              <div className="text-[11px] text-[var(--tir-muted)] mt-1">
                When your athletes log workouts or leave notes, they'll show up here.
              </div>
            )}
          </div>
        ) : (
          groupedItems.map((g, gIdx) => (
            <FeedGroupCard
              key={`group-${gIdx}-${g.key}`}
              group={g}
              groupIndex={gIdx}
            />
          ))
        )}
      </div>
    </section>
  );
}

/**
 * FeedGroupCard — renders either a single feed item (one row, same look
 * as the old un-grouped feed) or a collapsible "stack" card when an
 * athlete has multiple items on the same workout date. The collapsed
 * stack shows a one-line summary "+ 7.0 km · 45 min · 84 min" alongside
 * a chevron / count chip; click to expand and see every log and note.
 */
function FeedGroupCard({ group, groupIndex }) {
  const [expanded, setExpanded] = React.useState(false);
  const items = group.items || [];
  const lead = items[0];
  if (!lead) return null;
  // Header pulls from the lead item — it's the newest within the group
  // because the server returns items sorted by workout-date + submit
  // time, both DESC.
  const initials = (lead.athlete_name || "·")
    .split(/\s+/).slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("") || "·";
  // SINGLE-ITEM PATH — render the exact old row to preserve familiar UX.
  // NOTE: outer wrapper is a <div>, NOT a <Link>. Wrapping this in a Link
  // silently swallowed clicks on the inner workout-title <Link> and Reply
  // <button> (nested <a> inside <a> is invalid HTML, and the outer anchor
  // captures every click). The athlete-detail navigation is now a
  // dedicated affordance on the athlete's INITIALS avatar + name row
  // inside FeedItemBody so the coach still has one-tap access.
  if (group.single) {
    return (
      <div
        data-testid={`feed-item-${groupIndex}`}
        className="flex items-start gap-3 px-4 py-3 hover:bg-white transition-colors"
      >
        <Link
          to={`/coaching/athletes/${lead.athlete_id}`}
          data-testid={`feed-item-avatar-${groupIndex}`}
          className="w-9 h-9 rounded-full bg-[var(--tir-burgundy)] text-white flex items-center justify-center font-serif-display text-sm shrink-0 hover:bg-[#5e1521] transition-colors"
          aria-label={`Open ${lead.athlete_name || "athlete"} profile`}
        >
          {initials}
        </Link>
        <FeedItemBody it={lead} idx={groupIndex} showAthleteLink />
      </div>
    );
  }
  // GROUPED PATH — render a card with a collapsed summary by default.
  const logs = items.filter((it) => it.kind === "log");
  const notes = items.filter((it) => it.kind === "comment");
  const totalKm = logs.reduce((s, it) => s + (Number(it.distance_actual_km) || 0), 0);
  const totalMin = logs.reduce((s, it) => s + (Number(it.duration_actual_min) || 0), 0);
  // Per-workout title + actual stats. Title (prescribed) is bold; actual
  // is the lighter line beneath. Same shape the expanded view uses, so
  // the coach can scan the workouts without expanding the card.
  const rows = logs.map((it) => {
    const title = it.workout_title || it.workout_type || "Workout";
    const km = Number(it.distance_actual_km);
    const mn = Number(it.duration_actual_min);
    const bits = [];
    if (km > 0) bits.push(`${km.toFixed(1)} km`);
    if (mn > 0) bits.push(`${Math.round(mn)} min`);
    if (it.source === "strava_auto") bits.push("Strava");
    return { title, status: it.status, actual: bits.join(" · ") || "—", note: it.athlete_note, photos: it.photo_urls || [], sets: it.strength_sets || [] };
  });
  return (
    <div
      data-testid={`feed-group-${groupIndex}`}
      className="px-4 py-3 hover:bg-white transition-colors"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-[var(--tir-burgundy)] text-white flex items-center justify-center font-serif-display text-sm shrink-0" aria-hidden="true">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/coaching/athletes/${lead.athlete_id}`}
              className="text-[13px] font-semibold text-[var(--tir-ink)] hover:underline"
            >
              {lead.athlete_name}
            </Link>
            <span className="text-[10px] tracking-[0.18em] uppercase font-mono-tir text-[var(--tir-muted)]">
              {lead.week_number ? `W${lead.week_number}` : ""}
              {lead.day_label ? ` · ${lead.day_label}` : ""}
              {logs.length > 0 && (
                <span className="ml-1 text-[var(--tir-burgundy)]">
                  · {logs.length} workout{logs.length === 1 ? "" : "s"}
                </span>
              )}
              {notes.length > 0 && logs.length > 0 && (
                <span className="ml-1">· {notes.length} note{notes.length === 1 ? "" : "s"}</span>
              )}
            </span>
            <span className="text-[10px] font-mono-tir text-[var(--tir-muted)] ml-auto">
              {fmtTs(lead.ts)}
            </span>
          </div>
          {/* Collapsed summary — one row per workout: bold prescribed
              title + light actual stats. Plus a total line if there's
              more than one workout. */}
          {!expanded && (
            <div className="mt-1 space-y-1">
              {rows.map((r, ri) => (
                <div key={ri} className="flex items-start gap-1.5 leading-snug">
                  <StatusBadge s={r.status} />
                  <div className="min-w-0 flex-1">
                    <span className="text-[13px] font-bold text-[var(--tir-ink)]">{r.title}</span>
                    <span className="ml-2 text-[11px] font-mono-tir text-[var(--tir-muted)]">{r.actual}</span>
                    {r.note && (
                      <div className="text-[12px] text-[var(--tir-ink)] italic leading-snug line-clamp-2">&ldquo;{r.note}&rdquo;</div>
                    )}
                    {r.sets.length > 0 && (
                      <StrengthSetsSummary entries={r.sets} testid={`feed-group-${groupIndex}-sets-${ri}`} compact />
                    )}
                    {r.photos.length > 0 && (
                      <div className="mt-1"><LogPhotoStrip urls={r.photos} size="sm" testid={`feed-group-${groupIndex}-photos-${ri}`} /></div>
                    )}
                  </div>
                </div>
              ))}
              {rows.length > 1 && (totalKm > 0 || totalMin > 0) && (
                <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)] font-semibold pt-0.5 pl-6">
                  Total:{" "}
                  {totalKm > 0 && <>{totalKm.toFixed(1)} km</>}
                  {totalKm > 0 && totalMin > 0 && " · "}
                  {totalMin > 0 && <>{Math.round(totalMin)} min</>}
                </div>
              )}
            </div>
          )}
          {/* Notes are always visible in collapsed view — coach wants
              to see athlete notes at a glance per their request. */}
          {!expanded && notes.length > 0 && (
            <div className="mt-1.5 space-y-1">
              {notes.slice(0, 2).map((n, ni) => (
                <div
                  key={ni}
                  className="text-[12px] text-[var(--tir-ink)] italic flex items-start gap-1.5 leading-snug"
                >
                  <MessageCircle size={11} className="text-[var(--tir-burgundy)] shrink-0 mt-0.5" />
                  <span className="line-clamp-2">&ldquo;{n.body}&rdquo;</span>
                  {n.awaiting_reply && (
                    <span
                      className="text-[8px] tracking-[0.2em] uppercase font-bold text-white bg-[var(--tir-burgundy)] px-1.5 py-0.5 rounded-sm shrink-0"
                    >
                      Awaiting
                    </span>
                  )}
                </div>
              ))}
              {notes.length > 2 && (
                <div className="text-[10px] font-mono-tir text-[var(--tir-muted)]">
                  + {notes.length - 2} more note{notes.length - 2 === 1 ? "" : "s"}
                </div>
              )}
            </div>
          )}
          {/* Expanded view — full detail per item. */}
          {expanded && (
            <div className="mt-2 space-y-2.5">
              {logs.map((lg, li) => (
                <FeedItemBody it={lg} idx={`${groupIndex}-log-${li}`} key={`log-${li}`} compact />
              ))}
              {notes.map((nt, ni) => (
                <FeedItemBody it={nt} idx={`${groupIndex}-note-${ni}`} key={`note-${ni}`} compact />
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`feed-group-toggle-${groupIndex}`}
            className="mt-2 text-[9px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)] inline-flex items-center gap-1"
          >
            {expanded ? "Collapse" : `Show ${items.length} items`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * FeedItemBody — shared row body so the single-item path and the
 * expanded grouped path render the same shape. `compact=true` drops the
 * athlete-name header (the grouped card already shows it once).
 */
function FeedItemBody({ it, idx, compact = false, showAthleteLink = false }) {
  return (
    <div className="flex-1 min-w-0">
      {!compact && (
        <div className="flex items-center gap-2 flex-wrap">
          {showAthleteLink && it.athlete_id ? (
            <Link
              to={`/coaching/athletes/${it.athlete_id}`}
              data-testid={`feed-item-athlete-link-${idx}`}
              className="text-[13px] font-semibold text-[var(--tir-ink)] hover:text-[var(--tir-burgundy)] hover:underline"
            >
              {it.athlete_name}
            </Link>
          ) : (
            <span className="text-[13px] font-semibold text-[var(--tir-ink)]">
              {it.athlete_name}
            </span>
          )}
          <span className="text-[10px] tracking-[0.18em] uppercase font-mono-tir text-[var(--tir-muted)]">
            {it.kind === "comment" ? "Note" : "Log"}
            {it.week_number ? ` · W${it.week_number}` : ""}
            {it.day_label ? ` · ${it.day_label}` : ""}
          </span>
          <span className="text-[10px] font-mono-tir text-[var(--tir-muted)] ml-auto">
            {fmtTs(it.ts)}
          </span>
        </div>
      )}
      {it.kind === "log" ? (
        <div className="mt-1">
          {/* PRESCRIBED workout title — clickable → deep-links into the
              athlete's program editor scrolled to that specific workout.
              Underlined + burgundy so it reads as a real link on mobile
              (hover states don't exist on touch devices). */}
          {(it.workout_title || it.workout_type) && (
            it.program_id && it.workout_id ? (
              <Link
                to={`/coaching/programs/${it.program_id}/edit?week=${it.week_number || 1}&workout=${it.workout_id}`}
                data-testid={`feed-item-workout-link-${idx}`}
                className="inline text-[13px] font-bold text-[var(--tir-burgundy)] underline decoration-[var(--tir-burgundy)]/40 underline-offset-2 hover:decoration-[var(--tir-burgundy)] leading-snug"
              >
                {it.workout_title || it.workout_type}
                {it.workout_duration_or_distance && (
                  <span className="ml-1.5 text-[11px] font-mono-tir font-normal text-[var(--tir-muted)] tracking-wide no-underline">
                    {it.workout_duration_or_distance}
                  </span>
                )}
              </Link>
            ) : (
              <div className="text-[13px] font-bold text-[var(--tir-ink)] leading-snug">
                {it.workout_title || it.workout_type}
                {it.workout_duration_or_distance && (
                  <span className="ml-1.5 text-[11px] font-mono-tir font-normal text-[var(--tir-muted)] tracking-wide">
                    {it.workout_duration_or_distance}
                  </span>
                )}
              </div>
            )
          )}
          {/* ACTUAL — what the athlete actually did (or what Strava
              attached). Non-bold under the prescribed title. */}
          <div className="text-[12px] text-[var(--tir-muted)] mt-0.5 flex items-start gap-1.5 flex-wrap">
            <StatusBadge s={it.status} />
            <span className="font-mono-tir">
              {it.distance_actual_km != null && it.distance_actual_km > 0 && (
                <>{Number(it.distance_actual_km).toFixed(1)} km</>
              )}
              {it.duration_actual_min != null && it.duration_actual_min > 0 && (
                <> · {Math.round(it.duration_actual_min)} min</>
              )}
              {it.source === "strava_auto" && (
                <> · <span className="text-[#fc4c02]">Strava</span></>
              )}
            </span>
          </div>
          {it.athlete_note && (
            <div className="text-[12px] text-[var(--tir-ink)] italic mt-1 leading-snug" data-testid={`feed-item-note-${idx}`}>
              &ldquo;{it.athlete_note}&rdquo;
            </div>
          )}
          {it.strength_sets?.length > 0 && (
            <StrengthSetsSummary entries={it.strength_sets} testid={`feed-item-sets-${idx}`} compact />
          )}
          {it.photo_urls?.length > 0 && (
            <div className="mt-1.5"><LogPhotoStrip urls={it.photo_urls} size="sm" testid={`feed-item-photos-${idx}`} /></div>
          )}
          {/* Reply directly to the athlete's log note without opening the
              program. Any log with an athlete_note gets a Reply affordance
              — the coach's most common use case for the Recent Activity
              feed is answering these. */}
          {it.athlete_note && it.program_id && it.workout_id && (
            <InlineReply
              athleteId={it.athlete_id}
              programId={it.program_id}
              weekNumber={it.week_number}
              dayLabel={it.day_label}
              workoutId={it.workout_id}
              testIdx={idx}
            />
          )}
        </div>
      ) : (
        <div className="mt-1">
          <div className="text-[12px] text-[var(--tir-ink)] flex items-start gap-1.5 flex-wrap">
            <MessageCircle size={13} className="text-[var(--tir-burgundy)] shrink-0 mt-0.5" />
            <span className="leading-snug flex-1 min-w-0">&ldquo;{it.body}&rdquo;</span>
            {it.awaiting_reply && (
              <span
                className="inline-block text-[8px] tracking-[0.2em] uppercase font-bold text-white bg-[var(--tir-burgundy)] px-1.5 py-0.5 rounded-sm"
                data-testid={`feed-item-awaiting-${idx}`}
              >
                Awaiting reply
              </span>
            )}
          </div>
          {/* Inline reply — coach can respond directly from the overview
              instead of digging into the athlete's program to find the
              same comment thread. Only shown when we have enough context
              to know which workout the note belongs to. */}
          {it.program_id && it.workout_id && (
            <InlineReply
              athleteId={it.athlete_id}
              programId={it.program_id}
              weekNumber={it.week_number}
              dayLabel={it.day_label}
              workoutId={it.workout_id}
              testIdx={idx}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * InlineReply — small "Reply" affordance rendered under each comment in
 * the Recent Activity feed. Uses the existing `coachPostWorkoutComment`
 * endpoint so the note lands in the same workout thread the athlete can
 * see when they open the workout on their portal. Push notification is
 * fired server-side to the athlete's phone (or falls back to email).
 */
function InlineReply({ athleteId, programId, weekNumber, dayLabel, workoutId, testIdx }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      await coachPostWorkoutComment(athleteId, {
        program_id: programId,
        week_number: weekNumber,
        day_label: dayLabel,
        workout_id: workoutId,
        body,
      });
      setSent(true);
      setText("");
      setOpen(false);
      toast.success("Reply sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send reply");
    } finally {
      setSending(false);
    }
  };

  if (sent && !open) {
    return (
      <div
        className="mt-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold text-emerald-800 inline-flex items-center gap-1.5"
        data-testid={`feed-item-reply-sent-${testIdx}`}
      >
        <CheckCircle2 size={11} /> Reply sent
        <button
          type="button"
          onClick={() => {
            setSent(false);
            setOpen(true);
          }}
          className="ml-2 text-[var(--tir-navy)] hover:text-[var(--tir-ink)]"
        >
          Add another
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`feed-item-reply-open-${testIdx}`}
        className="mt-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-navy)] hover:text-[var(--tir-ink)] inline-flex items-center gap-1.5"
      >
        <MessageCircle size={11} /> Reply
      </button>
    );
  }

  return (
    <div className="mt-2 space-y-1.5" data-testid={`feed-item-reply-panel-${testIdx}`}>
      <textarea
        autoFocus
        rows={2}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Reply to this note…"
        data-testid={`feed-item-reply-textarea-${testIdx}`}
        className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-2.5 py-1.5 text-[12px] focus:outline-none resize-y leading-snug"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={sending || !text.trim()}
          data-testid={`feed-item-reply-send-${testIdx}`}
          className="inline-flex items-center gap-1 border border-[var(--tir-burgundy)] bg-[var(--tir-burgundy)] text-white px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase font-semibold hover:bg-[#5e1521] disabled:opacity-60"
        >
          {sending ? "Sending…" : "Send reply"}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setText("");
          }}
          disabled={sending}
          className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-ink)] px-1"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

